import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { Observable, switchMap, take } from "rxjs";
import { ConfigService } from "src/app/core/config/config-service";

/**
 * Hängt das Bearer-Token an Anfragen gegen die Vendor-Catalog-API.
 *
 * Das Token wird pro Anfrage frisch geholt statt zwischengespeichert. Die vorherige Fassung
 * abonnierte im Konstruktor `isAuthenticated$` und legte das Token in einem Feld ab. Daraus
 * ergaben sich zwei Rennen: Anfragen vor der ersten Emission — typischerweise aus Resolvern
 * beim ersten Seitenaufruf — gingen ohne Header raus, und nach einem Silent Renew lag
 * kurzzeitig noch das alte Token im Feld.
 */
@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    private readonly oidcSecurityService = inject(OidcSecurityService);
    private readonly configService = inject(ConfigService);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isApiRequest(request.url)) {
            return next.handle(request);
        }

        return this.oidcSecurityService.getAccessToken().pipe(
            take(1),
            switchMap(token => next.handle(token ? this.addToken(request, token) : request))
        );
    }

    /**
     * Nur Anfragen an die API bekommen ein Token.
     *
     * Eine Positivliste statt einer Ausschlussliste: Die API ist die einzige Gegenstelle des
     * Clients, ihre Basis-URL steht in der Laufzeit-Konfiguration. Alles andere — Assets,
     * Branding-Dateien, die Konfiguration selbst, der OIDC-Callback und der Authority-Host —
     * fällt damit automatisch heraus, ohne dass Hostnamen im Code stehen müssen.
     *
     * Solange die Konfiguration noch nicht geladen ist, wird kein Token angehängt. Das betrifft
     * genau die Anfrage, die die Konfiguration lädt.
     */
    private isApiRequest(url: string): boolean {
        const apiBaseUrl = this.configService.getConfig()?.urlVendorCatalogApi;
        return !!apiBaseUrl && url.startsWith(apiBaseUrl);
    }

    private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }
}
